import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThrottle } from '@toss/react';
import { CheckSmallCircleFillIcon, CheckSmallCircleIcon } from '@zep-us/zus-icons';
import cns from 'classnames';
import { useSpaceCategories } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { SpaceCategory } from '@zep/types';
import { Stack } from '@zep/ui';
import { cn } from '@zep/utils';
import { CategoryDropdownTrigger } from './CategoryDropdownTrigger';
import S from './MapTemplateModal.module.scss';

// ETC 카테고리는 UI에서 숨김 처리
const ETC_CATEGORY_ID = 8;
export const MapTemplateModalHeader = (props: MapTemplatesHeaderProps) => {
  const {
    tab,
    onTabChange,
    categoryId,
    onCategoryChange,
    isDropdownOpen,
    onDropdownOpenChange
  } = props;
  const {
    t,
    i18n
  } = useTranslation(['common']);
  const {
    user
  } = useAuth();
  const [isScrolling, setIsScrolling] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    data: {
      categories
    }
  } = useSpaceCategories({
    locale: i18n.resolvedLanguage ?? 'en'
  });
  const categoriesWithAll = useMemo<SpaceCategory[]>(() => [{
    id: -1,
    category: t('common:space.category.all'),
    visible: true,
    priority: 0,
    prefixIcon: '🌐'
  }, ...categories.filter(category => category.visible && category.id !== ETC_CATEGORY_ID)], [categories, t]);
  const onScroll = useThrottle((e: Event) => {
    if (e.target instanceof HTMLDivElement) {
      setIsScrolling(e.target.scrollTop > 0);
    }
  }, 100);
  useEffect(() => {
    const templateList = document.getElementById('template_modal_content');
    templateList?.addEventListener('scroll', onScroll);
    return () => templateList?.removeEventListener('scroll', onScroll);
  }, [onScroll]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && isDropdownOpen) {
        onDropdownOpenChange(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen, onDropdownOpenChange]);
  return <div className={cns(S.header_wrapper, {
    [S.header_wrapper_shadow]: isScrolling
  })} data-sentry-component="MapTemplateModalHeader" data-sentry-source-file="MapTemplateModalHeader.tsx">
      <Stack direction="row" data-sentry-element="Stack" data-sentry-source-file="MapTemplateModalHeader.tsx">
        <div className="relative box-border flex w-full shrink-0 items-start justify-start gap-2xs">
          <div ref={dropdownRef}>
            <CategoryDropdownTrigger label={categoriesWithAll.find(cat => cat.id === categoryId)?.category || t('common:space.category.all')} isCategorySelected={categoryId !== -1} disabled={tab === 'purchased'} isOpen={isDropdownOpen} onClick={() => onDropdownOpenChange(!isDropdownOpen)} data-sentry-element="CategoryDropdownTrigger" data-sentry-source-file="MapTemplateModalHeader.tsx" />
            {isDropdownOpen && <CategoryDropdownContent categories={categoriesWithAll} selectedCategoryId={categoryId} onCategorySelect={onCategoryChange} onClose={() => onDropdownOpenChange(false)} />}
          </div>
          <PurchasedMapButton isActive={tab === 'purchased'} onClick={() => onTabChange(tab === 'purchased' ? 'official' : 'purchased')} label={user?.isEdu ? t('common:mapTemplateModal.eduMap') : t('common:mapTemplateModal.purchaseMap')} data-sentry-element="PurchasedMapButton" data-sentry-source-file="MapTemplateModalHeader.tsx" />
        </div>
      </Stack>
    </div>;
};
const PurchasedMapButton = ({
  isActive,
  onClick,
  label
}: {
  isActive: boolean;
  onClick: () => void;
  label: string;
}) => {
  return <button className={cn('flex items-center gap-[2px] px-[12px] py-[10px] rounded-full', 'border border-gray-200', 'text-body-2 font-medium', isActive ? 'bg-[#F3F2FF] text-[#6758FF]' : 'bg-white text-gray-600')} onClick={onClick} type="button" data-sentry-component="PurchasedMapButton" data-sentry-source-file="MapTemplateModalHeader.tsx">
      {isActive ? <CheckSmallCircleFillIcon className="size-md text-[#6758FF]" /> : <CheckSmallCircleIcon className="size-md text-gray-500" />}
      <span className="block truncate">{label}</span>
    </button>;
};

// 카테고리 드롭다운 콘텐츠 컴포넌트
const CategoryDropdownContent = ({
  categories,
  selectedCategoryId,
  onCategorySelect,
  onClose
}: {
  categories: SpaceCategory[];
  selectedCategoryId: number;
  onCategorySelect: (categoryId: number) => void;
  onClose: () => void;
}) => {
  const handleCategorySelect = (categoryId: number) => {
    onCategorySelect(categoryId);
    onClose();
  };
  return <div className={cn('absolute left-0 right-0 top-full z-[70] h-fit', 'border border-t-gray-200 bg-white shadow-[0_1px_4px_0_rgba(42,39,65,0.08)]', 'w-screen -ml-[16px] mt-[12px]')} data-sentry-component="CategoryDropdownContent" data-sentry-source-file="MapTemplateModalHeader.tsx">
      <div className="overflow-y-auto px-[16px] py-[24px]">
        <div className="flex flex-col gap-xs">
          {categories.map(category => <button key={category.id} className={cn('w-full px-[14px] py-[9px] flex items-center gap-[6px] text-left rounded-[4px]', selectedCategoryId === category.id ? 'bg-[#F3F2FF]' : '')} onClick={() => handleCategorySelect(category.id)}>
              {category.prefixIcon && <span className="shrink-0 text-[16px]">
                  {category.prefixIcon}
                </span>}
              <span className={cn('text-subtitle font-medium', selectedCategoryId === category.id ? 'text-[#5748F2]' : 'text-[#5D5E69]')}>
                {category.category}
              </span>
            </button>)}
        </div>
      </div>
    </div>;
};
interface MapTemplatesHeaderProps {
  tab: string;
  onTabChange: (tab: 'official' | 'purchased') => void;
  categoryId: number;
  onCategoryChange: (categoryId: number) => void;
  isDropdownOpen: boolean;
  onDropdownOpenChange: (isOpen: boolean) => void;
}